import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAppContext } from '../../App';
import { api } from '../../apiService';
import { toast } from 'react-toastify';

function ClientForm({ onBack, clientToEdit }) {
    const { allStations, currentStationId } = useAppContext();
    
    const [isCheckingCnic, setIsCheckingCnic] = useState(false);
    const [cnicStatus, setCnicStatus] = useState({ available: true, message: '' });
    const [isCheckingContact, setIsCheckingContact] = useState(false);
    const [contactStatus, setContactStatus] = useState({ available: true, message: '' });
    const [isCheckingEmail, setIsCheckingEmail] = useState(false);
    const [emailStatus, setEmailStatus] = useState({ available: true, message: '' });
    
    const cnicCheckTimer = useRef(null);
    const contactCheckTimer = useRef(null);
    const emailCheckTimer = useRef(null);

    const getValidStationId = useCallback(() => {
        if (currentStationId && currentStationId !== 'all') return currentStationId;
        if (allStations.length > 0) return allStations[0].id;
        return '';
    }, [currentStationId, allStations]);

    const [formData, setFormData] = useState({ 
        name: '', 
        companyName: '', 
        contact: '', 
        email: '', 
        companyAddress: '', 
        cnic: '',
        ntn: '',
        stationId: getValidStationId()
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => { 
        if (clientToEdit) { 
            setFormData({
                name: clientToEdit.name || '',
                companyName: clientToEdit.companyName || '',
                contact: clientToEdit.contact || '',
                email: clientToEdit.email || '',
                companyAddress: clientToEdit.companyAddress || '',
                cnic: clientToEdit.cnic || '',
                ntn: clientToEdit.ntn || '',
                stationId: clientToEdit.stationId,
                id: clientToEdit.id
            });
        } else {
            setFormData(prev => ({
                name: '', companyName: '', contact: '', email: '', 
                companyAddress: '', cnic: '', ntn: '',
                stationId: getValidStationId() 
            }));
        }
    }, [clientToEdit, currentStationId, allStations, getValidStationId]);

    const checkDuplicate = useCallback(async (field, value, originalId = null) => {
        if (!value) return { available: true, message: '' };

        try {
            const results = await api.get('clients', { where: { [field]: value } });
            
            if (results.length === 0) {
                return { available: true, message: `✅ This ${field} is available.` };
            } else {
                const docData = results[0];
                if (originalId && docData.id === originalId) {
                     return { available: true, message: '' };
                }
                return { available: false, message: `⚠️ This ${field} is already registered with ${docData.name}.` };
            }
        } catch (err) {
            console.error(err);
            return { available: true, message: '' };
        }
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            if (clientToEdit) {
                await api.put('clients', clientToEdit.id, formData);
                toast.success('Client updated successfully');
            } else {
                await api.post('clients', formData);
                toast.success('Client added successfully');
            }
            onBack();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const handleChange = (e) => { 
        const { id, value } = e.target;
        let formattedValue = value;

        if (id === 'cnic') {
            const cnic = value.replace(/\D/g, '');
            formattedValue = cnic.length > 5 ? `${cnic.slice(0, 5)}-${cnic.slice(5, 12)}` : cnic;
            if (cnic.length > 12) formattedValue += `-${cnic.slice(12, 13)}`;
            
            clearTimeout(cnicCheckTimer.current);
            if (formattedValue.length === 15) {
                setIsCheckingCnic(true);
                cnicCheckTimer.current = setTimeout(async () => {
                   const status = await checkDuplicate('cnic', formattedValue, clientToEdit?.id);
                   setCnicStatus(status);
                   setIsCheckingCnic(false);
                }, 500);
            } else {
               setCnicStatus({ available: true, message: '' });
            }
        }

        if (id === 'contact') {
            const numbers = value.replace(/\D/g, '');
            if (numbers.startsWith('0')) {
                formattedValue = '92' + numbers.substring(1);
            } else if (numbers.startsWith('92')) {
                formattedValue = numbers;
            } else if (numbers.length > 0) {
                formattedValue = '92' + numbers;
            } else {
                formattedValue = numbers;
            }
            formattedValue = formattedValue.slice(0, 12); 

            clearTimeout(contactCheckTimer.current);
            if(formattedValue.length === 12){
                setIsCheckingContact(true);
                contactCheckTimer.current = setTimeout(async () => {
                    const status = await checkDuplicate('contact', formattedValue, clientToEdit?.id);
                    setContactStatus(status);
                    setIsCheckingContact(false);
                }, 500);
            } else {
                 setContactStatus({ available: true, message: '' });
            }
        }
        
        if (id === 'email') {
            formattedValue = value.trim();
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            clearTimeout(emailCheckTimer.current);
            if (!formattedValue) {
                setEmailStatus({ available: true, message: '' });
            } else if (emailRegex.test(formattedValue)) {
                setIsCheckingEmail(true);
                emailCheckTimer.current = setTimeout(async () => {
                    const status = await checkDuplicate('email', formattedValue, clientToEdit?.id);
                    setEmailStatus(status);
                    setIsCheckingEmail(false);
                }, 500);
            } else {
                setEmailStatus({ available: false, message: '⚠️ Invalid email format.' });
            }
        }

        setFormData({ ...formData, [id]: formattedValue }); 
    };




    
    const CheckingSpinner = () => <div className="absolute inset-y-0 right-0 pr-3 flex items-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-rose-500"></div></div>;

    return (
        <div className="container mx-auto">
             <div className="mb-8">
                <button onClick={onBack} className="text-rose-600 font-semibold flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    Back to Client List
                </button>
                <h1 className="text-4xl font-bold text-gray-800 mt-2">{clientToEdit ? 'Edit Client' : 'Add New Client'}</h1>
            </div>
            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-2xl shadow-lg space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div><label htmlFor="name" className="block text-sm font-medium text-gray-700">Client Name <span className="text-red-500">*</span></label><input type="text" id="name" value={formData.name} onChange={handleChange} className="mt-1 block w-full p-3 bg-gray-50 rounded-md" placeholder="e.g., Ali Ahmed" required /></div>
                    <div><label htmlFor="companyName" className="block text-sm font-medium text-gray-700">Company Name <span className="text-red-500">*</span></label><input type="text" id="companyName" value={formData.companyName} onChange={handleChange} className="mt-1 block w-full p-3 bg-gray-50 rounded-md" placeholder="e.g., Ali Traders" required /></div>
                    
                    <div>
                        <label htmlFor="contact" className="block text-sm font-medium text-gray-700">Contact Number (Unique) <span className="text-red-500">*</span></label>
                        <div className="relative">
                            <input type="tel" id="contact" value={formData.contact} onChange={handleChange} className="mt-1 block w-full p-3 bg-gray-50 rounded-md" placeholder="e.g., 923001234567" required maxLength="12" />
                            {isCheckingContact && <CheckingSpinner />}
                        </div>
                        {contactStatus.message && <p className={`text-sm mt-1 ${contactStatus.available ? 'text-green-600' : 'text-red-600'}`}>{contactStatus.message}</p>}
                    </div>
                    
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email Address (Unique) (Optional)</label>
                        <div className="relative">
                            <input type="email" id="email" value={formData.email} onChange={handleChange} className="mt-1 block w-full p-3 bg-gray-50 rounded-md" placeholder="e.g., info@alitraders.com" />
                            {isCheckingEmail && <CheckingSpinner />}
                        </div>
                        {emailStatus.message && <p className={`text-sm mt-1 ${emailStatus.available ? 'text-green-600' : 'text-red-600'}`}>{emailStatus.message}</p>}
                    </div>
                    
                    <div>
                        <label htmlFor="cnic" className="block text-sm font-medium text-gray-700">CNIC (Unique) <span className="text-red-500">*</span></label>
                        <div className="relative">
                           <input type="text" id="cnic" value={formData.cnic} onChange={handleChange} maxLength="15" className="mt-1 block w-full p-3 bg-gray-50 rounded-md" placeholder="e.g., 35202-1234567-8" required />
                           {isCheckingCnic && <CheckingSpinner />}
                        </div>
                        {cnicStatus.message && <p className={`text-sm mt-1 ${cnicStatus.available ? 'text-green-600' : 'text-red-600'}`}>{cnicStatus.message}</p>}
                    </div>

                    <div><label htmlFor="ntn" className="block text-sm font-medium text-gray-700">NTN (Optional)</label><input type="text" id="ntn" value={formData.ntn} onChange={handleChange} className="mt-1 block w-full p-3 bg-gray-50 rounded-md" placeholder="e.g., 1234567-8" /></div>
                    
                    <div className="md:col-span-2">
                        <label htmlFor="companyAddress" className="block text-sm font-medium text-gray-700">Company Address <span className="text-red-500">*</span></label>
                        <textarea id="companyAddress" value={formData.companyAddress} onChange={handleChange} rows="3" className="mt-1 block w-full p-3 bg-gray-50 rounded-md" placeholder="e.g., Shop # 1, Main Market, Lahore" required />
                    </div>

                    <div>
                        <label htmlFor="stationId" className="block text-sm font-medium text-gray-700">Assigned Station</label>
                        <input type="text" id="stationId" value={allStations.find(s => s.id === formData.stationId)?.name || 'No Station Selected'} className="mt-1 block w-full p-3 bg-gray-200 rounded-md text-gray-500" readOnly />
                    </div>
                </div>
                <div className="flex justify-end pt-6 border-t">
                    <button 
                        type="submit" 
                        disabled={isSubmitting || isCheckingCnic || isCheckingContact || isCheckingEmail || emailStatus.available === false} 
                        className="bg-rose-600 text-white font-bold py-3 px-8 rounded-lg shadow-lg hover:bg-rose-700 transition-all disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                        {isSubmitting ? 'Saving...' : (clientToEdit ? 'Save Changes' : 'Add Client')}
                    </button>
                </div>
            </form>
        </div>
    );
}

function ClientSection() {
    const [view, setView] = useState('list');
    const [editingClient, setEditingClient] = useState(null);
    const { currentStationId, allStations } = useAppContext();
    const [clients, setClients] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    
    const [pageSize, setPageSize] = useState(15);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalClients, setTotalClients] = useState(0);

    const fetchClients = useCallback(async () => {
        if (!currentStationId || currentStationId === 'all') {
            setClients([]);
            setTotalClients(0);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            // Simplified pagination for MySQL
            const results = await api.get('clients', { 
                where: { stationId: currentStationId },
                orderBy: 'createdAt DESC',
                // limit: pageSize, // Would need offset for proper pagination
            });
            
            setClients(results);
            setTotalClients(results.length);
        } catch (err) {
            setError("Error fetching clients.");
            console.error("API Error:", err);
        } finally {
            setIsLoading(false);
        }
    }, [currentStationId]);

    useEffect(() => {
        fetchClients();
    }, [currentStationId, pageSize, fetchClients]);

    const normalizedSearch = searchTerm.replace(/\s+/g, '').toLowerCase();
    const filteredClients = clients.filter(client =>
        (client.name && client.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (client.companyName && client.companyName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (client.cnic && client.cnic.includes(searchTerm)) ||
        (client.ntn && client.ntn.includes(searchTerm)) ||
        (client.contact && String(client.contact).replace(/\s+/g, '').toLowerCase().includes(normalizedSearch))
    );
    
    const handleEdit = (client) => { setEditingClient(client); setView('form'); }
    const handleAddNew = () => { setEditingClient(null); setView('form'); }
    
    const handleNextPage = () => {
        setCurrentPage(currentPage + 1);
        fetchClients('next');
    };

    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
            fetchClients('prev');
        }
    };
    
    if (view === 'form') {
        return <ClientForm onBack={() => { setView('list'); fetchClients('first'); }} clientToEdit={editingClient} />;
    }
    
    const pageStart = (currentPage - 1) * pageSize + 1;
    const pageEnd = Math.min(currentPage * pageSize, totalClients);

    return (
        <div className="container mx-auto">
            <div className="mb-8 flex justify-between items-center">
                <div><h1 className="text-4xl font-bold text-gray-800">Clients</h1><p className="text-gray-500 mt-1">Manage your client list and view their details.</p></div>
                <button onClick={handleAddNew} disabled={currentStationId === 'all'} className="bg-rose-600 text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:bg-rose-700 transition-all disabled:bg-gray-400 disabled:cursor-not-allowed">+ Add New Client</button>
            </div>
            
            <div className="bg-white p-6 rounded-2xl shadow-lg mb-8">
                <input type="text" placeholder="Search currently visible clients by name, company, CNIC, or NTN..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full bg-gray-50 border-gray-300 rounded-md shadow-sm p-3 font-semibold" />
            </div>

            {currentStationId === 'all' ? (
                 <div className="text-center p-10 bg-white rounded-2xl shadow-lg"><h3 className="text-xl font-bold text-gray-700">Please select a station</h3><p className="text-gray-500 mt-2">Select a station from the sidebar to view its clients.</p></div>
            ) : (
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left text-gray-500">
                            <thead className="text-xs text-gray-700 uppercase bg-gray-50"><tr><th className="px-6 py-3">Client / Company</th><th className="px-6 py-3">Contact</th><th className="px-6 py-3">CNIC / NTN</th><th className="px-6 py-3">Address</th><th className="px-6 py-3">Station</th><th className="px-6 py-3 text-center">Action</th></tr></thead>
                            <tbody>
                            {isLoading ? (<tr><td colSpan="6" className="text-center p-8">Loading...</td></tr>) 
                            : error ? (<tr><td colSpan="6" className="text-center p-8 text-red-500">{error}</td></tr>) 
                            : filteredClients.length > 0 ? filteredClients.map(client => (
                                <tr key={client.id} className="bg-white border-b hover:bg-gray-50">
                                    <td className="px-6 py-4"><div className="font-semibold text-gray-800">{client.name}</div><div className="text-xs text-gray-500">{client.companyName}</div></td>
                                    <td className="px-6 py-4"><div>{client.contact}</div><div className="text-xs text-gray-500">{client.email}</div></td>
                                    <td className="px-6 py-4"><div className="font-semibold">{client.cnic || 'N/A'}</div><div className="text-xs text-gray-500">NTN: {client.ntn || 'N/A'}</div></td>
                                    <td className="px-6 py-4">{client.companyAddress}</td>
                                    <td className="px-6 py-4">{allStations.find(s => s.id === client.stationId)?.name || 'N/A'}</td>
                                    <td className="px-6 py-4 text-center"><button onClick={() => handleEdit(client)} className="text-rose-600 hover:text-rose-800 font-semibold">Edit</button></td>
                                </tr>
                            )) : (<tr><td colSpan="6" className="text-center p-8">No clients found for this station.</td></tr>)}
                            </tbody>
                        </table>
                    </div>
                    
                    {totalClients > 0 && (
                        <div className="mt-4 p-4 flex justify-between items-center bg-white rounded-b-2xl border-t">
                             <div className="flex items-center gap-2">
                                <label htmlFor="pageSize" className="text-sm font-medium text-gray-700">Show:</label>
                                <select 
                                    id="pageSize" 
                                    value={pageSize} 
                                    onChange={(e) => setPageSize(Number(e.target.value))}
                                    className="p-2 bg-gray-50 border-gray-300 rounded-md shadow-sm font-semibold"
                                >
                                    <option value="15">15</option>
                                    <option value="30">30</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                            </div>
                            <span className="text-sm font-medium text-gray-700">
                                Showing {pageStart} - {pageEnd} of {totalClients}
                            </span>
                            <div className="flex gap-2">
                                <button 
                                    onClick={handlePrevPage} 
                                    disabled={currentPage === 1 || isLoading}
                                    className="px-4 py-2 text-sm font-semibold text-white bg-rose-600 rounded-md shadow disabled:bg-gray-300 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                <button 
                                    onClick={handleNextPage} 
                                    disabled={(currentPage * pageSize) >= totalClients || isLoading}
                                    className="px-4 py-2 text-sm font-semibold text-white bg-rose-600 rounded-md shadow disabled:bg-gray-300 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default ClientSection;
